import type es from '../es/agency';

/** `agency` in English. Same keys as `es/agency.ts`, enforced by the type. */
const agency: typeof es = {
  scope: 'Agency console',
  publishPosition: 'Post a job',
  publishFirstPosition: 'Post my first job',
  seeMyPositions: 'See my jobs',
  backToDashboard: 'Back to my dashboard',
  page: (n: number) => `Page ${n}`,
  emptyStage: (label: string) => `No one in ${label} yet`,

  layout: {
    loading: 'Loading your agency',
    brandSuffix: 'Agencies',
    activeAgency: 'Active agency',
    navLabel: 'Agency sections',
    nav: {
      overview: 'Overview',
      positions: 'Jobs',
      candidates: 'Candidates',
      team: 'Team',
    },
    suspended: {
      title: 'Your agency is suspended',
      body: 'To review your access, write to us from your account.',
    },
    loadError: {
      title: "We couldn't load your agency",
      retry: 'Try again',
      empty: 'Empty response from the server.',
    },
  },

  overview: {
    loading: 'Loading the overview',
    title: 'Overview',
    lede: 'Where your hiring stands today.',
    pendingNote: 'Suggested candidates will show up once your access is approved.',
    stats: {
      openPositions: 'Open jobs',
      seeAll: 'See all',
      suggestedNew: 'New suggestions',
      since: (date: string) => `Since ${date}`,
      shortlisted: 'Shortlisted',
      awaiting: 'Awaiting reply',
      awaitingDesc: 'Invitations sent, no answer yet',
      accepted: 'Accepted',
      acceptedDesc: 'You can reach out to them',
    },
    empty: {
      title: "You don't have any open jobs yet",
      note: 'Post one and we start looking for candidates in our pool as soon as you save it.',
    },
    nextStep: {
      scope: 'Next step',
      titleAccepted: 'Contact candidates who accepted',
      titleReview: 'Review your suggestions',
      seeAllCandidates: 'See all candidates',
      accepted:
        "Some candidates agreed to be contacted. Their email is on their card, in each job's Accepted tab.",
      shortlisted: "You already have people on your shortlist. Request their contact and we'll send the invitation for you.",
      none: "Open a job and shortlist the people who fit. No one finds out until you request their contact.",
    },
    howItWorks: {
      scope: 'How it works',
      title: 'The candidate decides',
      foot: "You'll never see an email nobody authorized you to see.",
      body: 'Profiles show no personal details. When you request contact, the person gets an invitation; their details appear only if they accept.',
    },
  },

  positions: {
    title: 'Jobs',
    lede: 'Each job looks for candidates on its own. Here you see where each one stands.',
    search: 'Search title or location',
    filterByStatus: 'Filter by status',
    filter: {
      all: 'All',
      open: 'Open',
      paused: 'Paused',
      filled: 'Filled',
    },
    columns: {
      title: 'Title',
      location: 'Location',
      wage: 'Pay',
      status: 'Status',
      suggested: 'Suggested',
      shortlisted: 'Shortlist',
      contacted: 'Contacted',
      accepted: 'Accepted',
      created: 'Created',
    },
    seeCandidates: 'See candidates',
    searchCandidates: 'Find candidates',
    noMatch: {
      title: 'No jobs match that filter',
      note: 'Try another title, location, or status.',
    },
    empty: {
      title: "You haven't posted any jobs yet",
      note: "Post the first one and we'll suggest candidates from our pool.",
    },
  },

  form: {
    titleEdit: 'Edit job',
    titleNew: 'New job',
    cancel: 'Cancel',
    required: 'Title, location, and description are required.',
    savedRefreshed: 'Changes saved. We searched for candidates again with the new description.',
    saved: 'Changes saved.',
    title: 'Title *',
    titlePlaceholder: 'e.g. Harvest worker',
    company: 'Company',
    companyPlaceholder: "Leave it blank and we'll use your agency's name",
    location: 'Location *',
    locationPlaceholder: 'e.g. Miami, FL',
    status: 'Status',
    jobType: 'Job type',
    jobTypeUnset: 'Not specified',
    jobTypeHint: "If you leave it unspecified, we won't rule anyone out based on this field.",
    description: 'Description *',
    descriptionPlaceholder: 'What the person does day to day, at what kind of company, with what team.',
    descriptionHint: 'We use this text to find candidates: the more specific, the better the profiles.',
    requirements: 'Requirements',
    requirementsPlaceholder: 'Experience, licenses, languages.',
    duties: 'Duties',
    dutiesPlaceholder: 'Specific tasks for the role.',
    wageFrom: 'Pay from',
    wageTo: 'To',
    wagePer: 'Per',
    wagePerOptions: {
      Hour: 'Hour',
      Week: 'Week',
      Month: 'Month',
      Year: 'Year',
    },
    workersNeeded: 'Openings',
    beginDate: 'Start',
    endDate: 'End',
    housing: 'Housing included',
    contactEmail: 'Contact email for this job',
    contactEmailPlaceholder: "Leave it blank and we'll use your agency's email",
    contactEmailHint: "This is your email, not the candidate's: you always reach them through the platform.",
    saveChanges: 'Save changes',
  },

  detail: {
    loading: 'Loading the job',
    notFound: {
      title: "We couldn't find this job",
      note: 'It may have been deleted, or it may belong to another one of your agencies.',
    },
    back: '← Jobs',
    edit: 'Edit',
    searchCandidates: 'Find candidates',
    searching: 'Finding candidates',
    found: (n: number) => (n === 1 ? '1 candidate found.' : `${n} candidates found.`),
    noneFound: 'There are no candidates that fit this job yet.',
    needsEmbedding:
      "We can't search for candidates for this job yet: we still need to process its description. Edit it and save again to retry.",
    stagesLabel: 'Hiring stages',
    contactVisible: 'These people agreed to be contacted. Their email is visible only here.',
    card: {
      scope: 'The job',
      title: 'Details',
      description: 'Description',
      requirements: 'Requirements',
      duties: 'Duties',
      summary: (workers: string, begin: string, end: string) =>
        `Openings: ${workers} · Start: ${begin} · End: ${end}`,
      housing: 'Housing included',
    },
  },

  actions: {
    invited: "We sent the candidate your invitation. If they accept, you'll see their contact details under Accepted.",
    conflict: 'That candidate already changed status. We refreshed the list.',
    inviteFailed: "We couldn't send the invitation. Try again in a moment.",
  },

  candidates: {
    title: 'Candidates',
    lede: 'All your candidates, across all your jobs, in whatever stage they are.',
    pendingLede: 'Your candidates become available once your access is approved.',
    filterLabel: 'Candidate status',
    loading: 'Loading candidates',
    contactVisible: 'These people agreed to be contacted: their email is on their card.',
  },

  create: {
    title: 'Create your agency or company',
    lede: 'Post your first job from your own console.',
    name: 'Name *',
    country: 'Country',
    countryOther: 'Other',
    countries: {
      US: 'United States',
      MX: 'Mexico',
      CO: 'Colombia',
      GT: 'Guatemala',
      HN: 'Honduras',
      SV: 'El Salvador',
      NI: 'Nicaragua',
      CR: 'Costa Rica',
      PA: 'Panama',
      DO: 'Dominican Republic',
      PE: 'Peru',
      EC: 'Ecuador',
      CL: 'Chile',
      AR: 'Argentina',
      BR: 'Brazil',
      ES: 'Spain',
    },
    website: 'Website (optional)',
    contactEmail: 'Contact email (optional)',
    note: 'You can post jobs right away. Seeing candidates requires approval, at no cost.',
    submitting: 'Creating…',
    submit: 'Create agency',
  },

  terms: {
    before: 'I accept the ',
    label: 'TBit Jobs Terms and Conditions',
    href: 'https://tbit.app/legal/tbit-jobs-terms-and-conditions',
    after: '.',
    required: 'Check the box to accept the terms and continue.',
    outdated: 'We updated the terms. Read them and check the box again.',
  },

  access: {
    requested: (date: string) =>
      `Request sent on ${date}. We'll email you once it's approved.`,
    intro:
      'You can already post jobs. To see candidates, request access: a person at TBit approves it, it has no cost, and it keeps candidates safe.',
    askAdmin: "To request it, ask your agency's admin.",
    pendingCount: (n: number) =>
      n === 1
        ? '1 suggested candidate will show up once your access is approved.'
        : `${n} suggested candidates will show up once your access is approved.`,
    sending: 'Sending…',
    request: 'Request access to candidates',
    gateTitle: 'Access to candidates requires approval',
  },

  team: {
    loading: 'Loading the team',
    title: 'Team',
    lede: 'Who can access this console and what they can do.',
    invalidEmail: 'Enter a valid email.',
    added: (email: string) =>
      `Done. ${email} now has a seat: they sign in with that email from the TBit sign-in page. We didn't send them any email.`,
    removed: (email: string) => `${email} no longer has access to this agency.`,
    membersTitle: 'People with access',
    seats: (n: number) => (n === 1 ? '1 seat' : `${n} seats`),
    empty: {
      title: "There's no one else yet",
      note: 'Add the people who work on jobs with you. They sign in with their own email.',
    },
    columns: {
      person: 'Person',
      role: 'Access',
      status: 'Status',
      since: 'Since',
    },
    you: ' · you',
    active: 'Active',
    notYet: 'Not signed in yet',
    lastAdmin: 'The agency has to keep at least one admin',
    remove: 'Remove',
    add: {
      scope: 'Add',
      title: 'Give someone else access',
      foot: "We don't send an invitation email: the person signs in to TBit with this same email and the console is already there.",
      email: 'Email *',
      emailPlaceholder: 'person@youragency.com',
      name: 'Name',
      namePlaceholder: 'Optional',
      role: 'Access',
      submit: 'Add to team',
    },
    recruiterNote:
      'Only the agency admin can add or remove people. If you need to bring someone on, ask them.',
    roles: {
      admin: 'Admin',
      recruiter: 'Recruiter',
    },
    roleNotes: {
      admin: 'Posts jobs, sees candidates, and manages the team.',
      recruiter: "Posts jobs and sees candidates. Can't add or remove people.",
    },
    errors: {
      invite403: 'Only an admin can add people to the team.',
      invite409: 'That email already has a seat in this agency.',
      invite400: 'Check the email and the access level: one of them is not valid.',
      inviteGeneric: "We couldn't add that person. Try again.",
      remove403: 'Only an admin can remove people from the team.',
      remove409:
        'The agency has to keep at least one admin. Make someone else an admin before removing this person.',
      remove404: 'That seat no longer exists.',
      removeGeneric: "We couldn't remove that person. Try again.",
    },
  },

  matchCard: {
    positionDeleted: 'Job deleted',
    experience: (years: number) => (years === 1 ? '1 year of experience' : `${years} years of experience`),
    affinity: 'match',
    lookingFor: (roles: string) => `Looking for: ${roles}`,
    in: (places: string) => `In: ${places}`,
    invitedOn: (date: string) =>
      `We sent your invitation on ${date}. We'll let you know here when they reply.`,
    declined: 'The candidate chose not to share their details for this job.',
    accepted: 'Agreed to be contacted',
    shortlist: 'Shortlist',
    requestContact: 'Request contact',
    dismiss: 'Dismiss',
  },

  jobTypes: {
    full_time: 'Full time',
    part_time: 'Part time',
    seasonal: 'Seasonal',
    temporary: 'Temporary',
    contract: 'Contract',
  },
  matchStatus: {
    suggested: 'Suggested',
    shortlisted: 'Shortlisted',
    contact_requested: 'Invitation sent',
    candidate_accepted: 'Accepted contact',
    candidate_declined: 'Declined contact',
    dismissed: 'Dismissed',
  },
  positionStatus: {
    open: 'Open',
    paused: 'Paused',
    filled: 'Filled',
  },
  pipeline: {
    suggested: {
      label: 'Suggested',
      hint: 'Find candidates and the profiles closest to this job will show up here. We only show people who agreed to appear in the talent pool.',
    },
    shortlisted: {
      label: 'Shortlist',
      hint: "From Suggested, shortlist the people you want to review more closely. No one finds out you shortlisted them.",
    },
    contact_requested: {
      label: 'Contacted',
      hint: "When you request contact for someone on your shortlist, we send them the invitation and you'll see them here while they reply.",
    },
    candidate_accepted: {
      label: 'Accepted',
      hint: 'People who agreed to be contacted show up here, with their email. No one gets here on their own: you invite them from Contacted first.',
    },
    candidate_declined: {
      label: 'Declined',
      hint: "No one has declined your invitation. If someone does, they show up here and don't go back into the stages.",
    },
    dismissed: {
      label: 'Dismissed',
      hint: 'Anyone you dismiss stays here in case you change your mind: you can shortlist them again from their card.',
    },
  },
  unnamedCandidate: 'Candidate with no name on their CV',
  candidateRef: (tail: string) => `Candidate #${tail}`,
  candidate: 'Candidate',
};

export default agency;
